/**
 * @file Surface state
 */
import { trackEvent } from '@@/bits/analytics'
import { shadeColor } from '@@/bits/color'
import { captureException, captureNonNetworkFetchError, setScope } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { isCid, removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import { clearSearchParam, getSearchParam, navigateTo } from '@@/bits/location'
import { vSet } from '@@/bits/vue'
import { ParamCommentModerationActionResult, ParamPostModerationActionResult, WallVizEnum } from '@@/enums'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContributingStatusStore } from '@@/pinia/surface_contributing_status'
import { useSurfaceExpandedCommentStore } from '@@/pinia/surface_expanded_comment'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { TRANSLATION_QUERY_PARAM_KEY, useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type { Cid, GoogleAppLicensingValidateResponse, Id, Post } from '@@/types'
import { useSurfaceCommentDrawer } from '@@/vuecomposables/useSurfaceCommentDrawer'
import post from '@@/vuexstore/modules/post'
import realtime from '@@/vuexstore/modules/realtime'
import settings from '@@/vuexstore/modules/surface_settings'
import logger, { shouldLog } from '@@/vuexstore/plugins/logger'
import type { RootState, SurfaceState } from '@@/vuexstore/surface/types'
import type { ContentModeration, JsonAPIResource, User } from '@padlet/arvo'
import { GroupByTypes } from '@padlet/arvo'
import { merge } from 'es-toolkit'
import type { Plugin, StoreOptions } from 'vuex'

// How to decide what to put in state vs getters
// State should have either:
// 1. things we sync with the server
// 2. things that change
// 3. things that are a function of the device
// Everything else is in getters.

// Keep all lists alphabetical
const plugins: Array<Plugin<RootState>> = [
  // pepinTracker    // Uncomment to track all state changes
]
if (shouldLog) plugins.push(logger)

export const blankState = (): SurfaceState => ({
  /* CONSTANTS */
  arvoConfig: undefined,
  constants: {},
  cookiesEnabled: false,
  csrfToken: '',
  defaultPdfPageSize: 'a4',
  deviceId: '',
  dir: '',
  dpr: 1,
  environment: 'production',
  isAndroid: false,
  isApp: false,
  isChrome: false,
  isDesktop: false,
  isElectronApp: false,
  isEdge: false,
  isEmbedded: false,
  isLtiEmbedded: false,
  isGoogleClassroomEmbedded: false,
  googleClassroomAuthorIdFilter: '',
  isMicrosoftTeamsEmbedded: false,
  isWhiteboardEmbedPlayMode: false,
  isFirefox: false,
  isIE: false,
  isIOS: false,
  isIPhone: false,
  isIPhoneX: false,
  isMac: false,
  isMobile: false,
  isMousable: false,
  isOldApp: false,
  isPhone: false,
  isSafari: false,
  isTablet: false,
  isTouchable: false,
  isWindows: false,
  locale: 'en',
  nativePostEdit: false,
  release: '',
  uid: '',
  /* OBJECTS */
  tenant: {},
  user: {},
  wall: {},

  /* OTHERS */
  commentsPanelPostId: null,
  wallLastSyncTime: null,
  isSlideshow: false,
  isFieldTrip: false,
  isGeneratingPDF: false,
  isSubmissionRequest: false,
  isFirstView: false,
  magicWallType: null,
  isUsedInEducationalContext: false,
  xFilterProfanitySetting: false,
  postHeights: {},

  /* PANELS */
  xCommentsPanel: false,
  isSkoletubeUser: false,
  isNewWhiteboardUser: false,

  /* PERMISSIONS */
  amIOwner: false,
  amILibraryOwner: false,
  canIAdminister: false,
  canIEdit: false,
  canIMakeWalls: false,
  canIModerate: false,
  canIRemake: false,
  canIComment: false,
  canIReact: false,
  canIWrite: false,
  canIAccessContentDirectly: true,
  isSubscriptionPaused: false,
  isReadOnly: true,
  isScreenshotMode: false,
  isSimplifiedView: false,
  xAuthorInScreenshotMode: true,
  xCommentsInScreenshotMode: true,
  xReactionsInScreenshotMode: true,
  xLogoInScreenshotMode: true,
  amICollaborator: false,
  blockedContentProviders: new Set<string>(),
  canUseLibraryAsMember: false,
  canMakeWallInTenant: false,
  canInviteStudents: false,
  canUseLibraryAsTeacher: false,

  /* LIBRARIES */
  library: {},

  /* EMBED */
  loginAndRedirectToWallUrl: '',
  loginAndRedirectToBuilderProfileUrl: '',
  loginAndRedirectUrlExpireDuration: 0,

  shouldFallbackWsDataFetchToRest: false,

  /* TRANSFER WALL */
  isWallTransferable: false,
})

export const storeStructure: StoreOptions<RootState> = {
  plugins,
  modules: {
    post,
    settings,
    realtime,
  },
  state: blankState() as RootState,
  getters: {
    // constants
    sectionableLayouts: (state) => state.constants.sectionableLayouts,
    // wall
    wall: (state) => state.wall,
    displayAttrs: (state) =>
      useSurfaceSettingsStore().isPreviewing ? useSurfaceSettingsStore().previewAttributes : state.wall,
    wallId: (state) => state.wall.id,
    wallHashid: (state) => state.wall.hashid,
    wallUpdatedAt: (state) => state.wall.updated_at,
    wallCreatedAt: (state) => state.wall.created_at,
    title: (state, getters) => getters.displayAttrs.title,
    description: (state, getters) => getters.displayAttrs.description,
    background: (state, getters) => getters.displayAttrs.background,
    portrait: (state, getters) => getters.displayAttrs.portrait,
    viz: (state, getters) => getters.displayAttrs.viz, // reflects true WallViz. (for styling, use format)
    contentModerationSetting: (state, getters): ContentModeration => getters.displayAttrs.content_moderation,
    user: (state) => state.user,
    requireApproval: (state, getters) => getters.displayAttrs.require_approval,
    isFrozen: (state) => state.wall.frozen === true,
    // Background/skin, the skin colors are based on the background for now but they may change in the future
    backgroundColor: (state, getters) =>
      getters.displayAttrs.background.primary_color_as_rgb ?? getters.displayAttrs.background.dominant_color_as_rgb,
    backgroundLuminance: (state, getters) => getters.displayAttrs.background.luminance_name,
    isBackgroundBlurred: (state, getters) =>
      Array.isArray(getters.background.effects)
        ? getters.background.effects.includes('blur')
        : getters.background.effect === 'blur',
    skinPrimaryColorAsRGB: (state, getters) => getters.displayAttrs.background.primary_color_as_rgb,
    skinPrimaryVariantColorAsRGB: (_, getters) => {
      if (getters.skinPrimaryColorAsRGB != null) {
        return getters.skinPrimaryColorAsRGB
      }
      const originalColor = getters.displayAttrs.background.dominant_color_as_rgb
      if (getters.displayAttrs.background.type === 'solid_color' && originalColor != null) {
        // 25% ligher for light backgrounds, 10% darker for dark backgrounds
        return shadeColor(originalColor, getters.backgroundLuminance === 'light' ? 25 : -10)
      }
      return originalColor
    },
    skinAccentColorAsRGB: (state, getters) => getters.displayAttrs.background.accent_color_as_rgb,
    skinPrimaryTextColor: (state, getters) => getters.displayAttrs.background.primary_text_color,
    skinAccentTextColor: (state, getters) => getters.displayAttrs.background.accent_text_color,

    colorScheme: (state, getters) => getters.displayAttrs.color_scheme,
    themeId: (state, getters) => getters.displayAttrs.theme_id, // used for map
    fontId: (state, getters) => getters.displayAttrs.font_id,
    isCommentable: (state, getters) => getters.displayAttrs.is_commentable,
    isReactable: (state, getters) => getters.displayAttrs.is_reactable,
    isRemakeable: (_, getters) => getters.displayAttrs.is_remakeable,
    reactionData: (state, getters) => getters.displayAttrs.reaction_data,
    freezeBannerInformation: (state, getters) => getters.displayAttrs.freeze_banner_information,
    name: (state, getters) => getters.displayAttrs.name,
    xAuthor: (state, getters) => getters.displayAttrs.show_author,
    isTitleBarTransparent: (state, getters) => Boolean(getters.displayAttrs.is_title_bar_transparent),
    isGeneratingPDF: (state) => state.isGeneratingPDF,
    isSubmissionRequest: (state) => state.isSubmissionRequest,

    builder: (state) => state.wall.builder,
    namespace: (state) => state.wall.namespace,
    isOrgWall: (state) => state.wall.tenant_id > 1,
    coverPostId: (state) => state.wall.cover_wish_id,
    publicKey: (state) => state.wall.public_key,
    address: (state) => state.wall.address,
    canonicalAddress: (state) => state.wall.canonical_address ?? state.wall.address,
    domainName: (state) => state.tenant.domain_name,
    tenantName: (state) => state.tenant.name,
    isTemplate: (state) => state.wall.is_template,

    // Permissions
    canIAdminister: (state) => state.canIAdminister,
    canIModerate: (state) => state.canIModerate,
    canIComment: (state) => state.canIComment,
    canIReact: (state) => state.canIReact,
    canIWrite: (state) => state.canIWrite,
    canIEdit: (state) => state.canIEdit,
    canIMakeWalls: (state) => state.canIMakeWalls,
    canIRemake: (state) => state.canIRemake,
    amIOwner: (state) => state.amIOwner,
    amICollaborator: (state) => state.amICollaborator,
    amIRegistered: (state) => !!state.user.registered_at,
    canICreateWall: (state) => state.user.quota.can_make,
    canIPostWithoutWaitingForApproval: (state, getters) =>
      getters.canIWrite && (getters.canIModerate || !getters.requireApproval),
    isApp: (state) => state.isApp,
    isPhone: (state) => state.isPhone,
    isDesktop: (state) => state.isDesktop,
    isMousable: (state) => state.isMousable,
    dir: (state) => state.dir,
    isReadOnly: (state) => state.isReadOnly,
    isScreenshotMode: (state) => state.isScreenshotMode,
    canIPost: (state, getters) =>
      (state.canIWrite && !state.isReadOnly && !(getters.isFrozen as boolean)) || state.isSubmissionRequest,
    format: (state, getters) => {
      // reflects layout format for display styling (for true wall format, use viz)
      if (useSurfaceShareLinksStore().isBreakout) return getters.displayAttrs.viz === 'map' ? 'map' : 'grid'
      return getters.displayAttrs.viz
    },
    isCanvas: (state, getters) => getters.format === 'free',
    isMap: (state, getters) => getters.format === 'map',
    isShelf: (state, getters) => getters.format === 'shelf',
    isStream: (state, getters) => getters.format === 'stream',
    isGrid: (state, getters) => getters.format === 'grid',
    isTable: (state, getters) => getters.format === 'table',
    isTimeline: (state, getters) => getters.format === 'timeline' || getters.format === 'timeline_v2',
    isTimelineV1: (state, getters) => getters.format === 'timeline',
    isRows: (state, getters) => getters.format === 'timeline_v2',
    isMatrix: (state, getters) => getters.format === 'matrix',
    isColumns: (state, getters) => getters.format === 'columns',
    isWhiteboard: (state, getters) => getters.viz === WallVizEnum.Whiteboard,
    isSectioned: (_state, getters) => (getters.isShelf as boolean) || getters.wishGroupBy === GroupByTypes.Section,
    xUnpublishedPostPosition: (state, getters) => getters.isTable,
    links: (state) => state.wall.links,
    xSettingsPanel: () => useSurfaceSettingsStore().isOpen,
    isOverlayVisible: (state, getters) => {
      const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
      const surfacePanelsStore = useSurfacePanelsStore()
      return (
        // Side panels
        surfacePanelsStore.xSurfaceDetailsPanel || // About this padlet panel
        surfacePanelsStore.xReportPanel ||
        surfacePanelsStore.xDevelopersPanel ||
        getters.xSettingsPanel ||
        surfacePanelsStore.xSurfaceSharePanel ||
        useSurfacePostActionStore().xTransferPostPanel ||
        useSurfacePostActionStore().xCopyPostPanel ||
        surfacePanelsStore.xSurfaceActivityPanel ||
        surfacePanelsStore.xSurfaceRemakePanel ||
        useSurfaceFreezePadletPanel().xSurfaceFreezePanel ||
        useSurfaceMapStore().isPickingLocation ||
        surfacePanelsStore.xSurfaceAutomationsPanel ||
        surfacePostPropertiesStore.xVideoRecorderSettingsPanel ||
        surfacePostPropertiesStore.xScreenRecorderSettingsPanel ||
        surfacePostPropertiesStore.xCustomPostPropertiesPanel ||
        useSurfaceTranslationsStore().xSurfaceLanguagePanel ||
        surfacePanelsStore.xPhoneFieldMethodPopover ||
        // Modals
        useSurfaceCommentDrawer().xAddCommentDrawer.value || // Add comment on mobile
        useSurfaceCommentDrawer().xEditCommentDrawer.value || // Edit comment on mobile
        useReactionsStore().postIdBeingReactedTo || // Reaction modal on mobile
        useSurfaceUserContributorsStore().xContributorsList ||
        useSurfaceStore().xWallQuotaExceeded ||
        surfacePanelsStore.xEditBookmarksDialog ||
        useContentPickerStore().xContentPickerPanel ||
        surfacePanelsStore.xPhotoAlbumImagePicker ||
        useLibraryPlansStore().xUpgradeModal ||
        useLibraryPlansStore().plans.gold.annual.currency?.toLowerCase() ||
        usePersonalPlansStore().xUpgradeModal ||
        useSurfacePostActionStore().xPostAction ||
        useSurfaceFilterStore().xFilterModal ||
        useSurfaceSectionsStore().xSectionRenameModal ||
        useSurfacePostGroupByStore().xGroupRenameModal ||
        useSurfaceGuestStore().xGuestIdCard ||
        surfacePanelsStore.xQrModal ||
        surfacePanelsStore.xPhotoAlbumEditorDialog ||
        useAppBarAccountStore().xUserMenu ||
        useSurfaceRequireNoticeStore().xExtensionRequired ||
        useSurfaceStore().xPermitNotification ||
        useAccessibilitySettingsStore().xAccessibilitySettingsModal ||
        useScreenRecorderStore().isVisible ||
        // Menus
        surfacePanelsStore.xActionBarMoreMenu ||
        surfacePanelsStore.xEmojiPicker ||
        useSurfaceSectionsStore().sectionUnderActionId ||
        useSurfacePostGroupByStore().groupUnderActionId != null ||
        // Dialogs
        useGlobalAlertDialogStore().isOpen ||
        useGlobalConfirmationDialogStore().isOpen ||
        // Expanded Post
        useExpandedPostStore().xExpandedPostPanel ||
        // Comments
        useCommentsStore().commentUnderActionId != null ||
        useSurfaceExpandedCommentStore().xExpandedCommentImagePanel ||
        // Search bar
        useSurfaceStore().xSearchBar ||
        // Translations drawer on mobile
        useSurfaceTranslationsStore().xMobileTranslationBanner ||
        // Legacy
        state.xCommentsPanel
      )
    },
    isWallSyncNeeded: (state, getters) => {
      const lastSycnedTime = state.wallLastSyncTime
      if (!lastSycnedTime) return true
      return getters['realtime/isRealtimeDownSinceLastSync'](lastSycnedTime)
    },

    // pagination - only posts for now
    isInitialPaginatedDataLoadDone: (state, getters) => getters['post/isInitialPaginatedDataLoadDone'],
    wishGroupBy: (state, getters) => {
      if (!useSurfaceStore().canUseSections) return GroupByTypes.None
      return getters.displayAttrs.wish_arrangement?.group_by ?? GroupByTypes.None
    },
    wishSortBy: (state, getters) => {
      return getters.displayAttrs.wish_arrangement?.sort_by
    },

    /* User Oauth Token */
    oauthToken: (state) => state.arvoConfig != null && state.arvoConfig.token && state.arvoConfig.token.oauthToken,

    // Libraries
    isLibraryWall: (state) => Boolean(state.wall.library_id),
    library: (state) => state.library,
    libraryName: (state) => state.library?.name,
    librarySlug: (state) => state.library?.slug,
    libraryDashboardUrl: (state) => state.library?.dashboard_url,
    libraryType: (state) => state.library?.libraryType,
    // Embed
    loginAndRedirectToWallUrl: (state) => state.loginAndRedirectToWallUrl,
    loginAndRedirectToBuilderProfileUrl: (state) => state.loginAndRedirectToBuilderProfileUrl,
    loginAndRedirectUrlExpireDuration: (state) => state.loginAndRedirectUrlExpireDuration,
  },
  mutations: {
    INITIALIZE_STATE(state, data) {
      Object.assign(state, merge(merge({}, state), data))
    },
    HIDE_COMMENTS_PANEL(state) {
      state.xCommentsPanel = false
      state.commentsPanelPostId = null
    },
    SHOW_COMMENTS_PANEL(state, postId) {
      state.xCommentsPanel = true
      state.commentsPanelPostId = postId
    },
    UPDATE_WALL(state, changes) {
      // Do this so that the whole wall object will not be reactively updated
      // but rather just each of the individual properties will be.
      Object.keys(changes).forEach((c) => {
        vSet(state.wall, c, changes[c])
      })
    },
    REFRESH_DONE(state) {
      state.wallLastSyncTime = Date.now()
    },
    SET_SHOULD_USE_REST_API(state, shouldFallbackWsDataFetchToRest: boolean): void {
      state.shouldFallbackWsDataFetchToRest = shouldFallbackWsDataFetchToRest
    },
    UPDATE_USER(state, user: User): void {
      state.user = merge(state.user, user)
    },
  },
  actions: {
    initializeState({ commit, dispatch, getters }, data) {
      const {
        postAction: { expandedPostCidOrHashid },
        brahmsToken,
        deviceId,
        firstViewAfterInviteAccepted,
        paramPostModerationActionResult,
        paramCommentModerationActionResult,
        previouslyViewedLanguage,
        ...rootStoreState
      } = data

      const userId = rootStoreState.user.id
      const wallId = rootStoreState.wall.id

      commit('INITIALIZE_STATE', rootStoreState)

      // We add a setTimeout since we want to delay the execution of the code below until pinia is available
      setTimeout(() => {
        // Log that the user has created a padlet. We are doing this to see user journeys.
        if (useSurfaceStore().isFirstView) {
          trackEvent('Surface', 'Created wall', rootStoreState.wall.title, null, { wallId })
        }
      }, 0)

      void dispatch('realtime/initializeState', {
        brahmsToken,
        deviceId,
        userId,
      })
      if (expandedPostCidOrHashid != null) {
        // Systemic Authorization:
        // we now use hashid for expanded posts, but old links using id should still work
        if (isCid(expandedPostCidOrHashid)) {
          // We can preemptively expand post, since all wish data will be fetched when a surface loads
          // Enqueue in event loop to ensure that it runs only after the pinia store is already created
          // https://stackoverflow.com/a/779785
          setTimeout(() => {
            useExpandedPostStore().expandPost({ postCid: expandedPostCidOrHashid, xTextPanel: true })
          }, 0)
        } else {
          const wishHashid = expandedPostCidOrHashid
          dispatch('post/fetchSinglePostWithHashid', { wishHashid })
            .then((wish) => {
              if (wish != null) {
                // Use result to expand target post
                useExpandedPostStore().expandPost({ postCid: `c${wish.id as Id}`, xTextPanel: true })
              }
            })
            .catch((error) => {
              // If this call fails, if postEntities are available, search post matching hashid and expand
              const postEntities: Record<Cid, Post> = getters['post/postEntitiesByCid']
              if (postEntities != null) {
                const matchingPost = Object.values(postEntities).find(
                  (p: Post) => p.hashid != null && removeHashidPrefix(p.hashid) === wishHashid,
                )
                if (matchingPost != null) {
                  useExpandedPostStore().expandPost({ postCid: matchingPost.cid, xTextPanel: true })
                }
              }
              captureException(error)
            })
        }
      }

      if (getters.amIRegistered === true) {
        // We add a setTimeout since we want to delay the execution of the code below until pinia is available
        setTimeout(() => {
          void import('@@/pinia/surface_collections_store').then(({ useSurfaceCollectionsStore }) => {
            const surfaceCollectionsStore = useSurfaceCollectionsStore()
            surfaceCollectionsStore.initialize() // initializes shared collections store which surfaceCollectionsStore depends on
            surfaceCollectionsStore.setActiveWall(wallId)
            void surfaceCollectionsStore.fetchBookmarks({ wallId })

            const surfaceGuestStore = useSurfaceGuestStore()
            if (surfaceGuestStore.shouldEnableAnonymousAttribution && rootStoreState.user.just_registered === true) {
              surfaceGuestStore.showSnackbarAfterSigningUp()
            }

            if (rootStoreState.user.just_registered === true) {
              void useAccessibilitySettingsStore().syncLocalSettingsToServer()
            }
          })
        }, 0)
      }

      const defaultSectionId = rootStoreState.wall?.wish_arrangement?.default_section_id
      if (defaultSectionId != null) {
        setTimeout(() => {
          try {
            useSurfaceSectionsStore().setDefaultSectionAndRecentlyTouchedSection(defaultSectionId)
          } catch (e) {
            captureException(e, {
              context: {
                error: 'Error setting default section and recently touched section',
                pinia: window.app?.$pinia ?? 'window.app.$pinia is null or undefined',
              },
            })
          }
        }, 0)
      }

      // Show invite accepted toast if first view after invite accepted.
      if (firstViewAfterInviteAccepted === true) {
        setTimeout(() => {
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.success,
            message: __('Invite accepted'),
            timeout: 3000,
          })
        })
      }

      // this block used isWhiteboard which uses viz which uses displayAttrs that depends on the pinia store
      // => put it under setTimeout since we want to delay the execution until pinia is available
      setTimeout(() => {
        if (isAppUsing('translatePadlet') && getters.isWhiteboard === false) {
          setTimeout(() => {
            void useSurfaceTranslationsStore().detectLanguage()

            // To handle the case where user clicks on slideshow from a loaded surface, since the surface is already loaded
            // and won't trigger the initial load event
            const lang = getSearchParam(TRANSLATION_QUERY_PARAM_KEY)
            if (lang != null) {
              void useSurfaceTranslationsStore().translatePadletFromBanner(lang)
            } else if (previouslyViewedLanguage != null) {
              void useSurfaceTranslationsStore().translatePadletFromBanner(previouslyViewedLanguage)
            }
          })
        }
      }, 0)

      void dispatch('handleSlideshowQrCodeParams')
      setTimeout(() => {
        void dispatch('handlePostModerationParams', paramPostModerationActionResult)
        void dispatch('handleCommentModerationParams', paramCommentModerationActionResult)
      })
      if (isAppUsing('googleAppLicensing') && getters.isOrgWall === true) {
        void dispatch('validateGoogleAppLicense')
      }
    },
    refreshPadletStartingState({ commit }, payload) {
      commit('INITIALIZE_STATE', payload)
    },
    handleCommentModerationParams(_context, paramCommentModerationActionResult: ParamCommentModerationActionResult) {
      switch (paramCommentModerationActionResult) {
        case ParamCommentModerationActionResult.APPROVE_SUCCESS:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.success,
            message: __('Comment approved'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.REJECT_SUCCESS:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.error,
            message: __('Comment rejected'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.LOGIN_REQUIRED:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.error,
            message: __('Please log in to your account'),
            timeout: 3000,
          })
          break
        case ParamCommentModerationActionResult.MODERATE_FAIL:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.error,
            message: __('Unable to moderate comment'),
            timeout: 3000,
          })
          break
      }
      clearSearchParam('moderate_comment_id', false)
      clearSearchParam('moderate_comment_action', false)
    },
    handlePostModerationParams(_context, paramPostModerationActionResult: ParamPostModerationActionResult) {
      // Show result of post reject/approve from query param
      switch (paramPostModerationActionResult) {
        case ParamPostModerationActionResult.APPROVE_SUCCESS:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.success,
            message: __('Post approved'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.REJECT_SUCCESS:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.success,
            message: __('Post rejected'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.LOGIN_REQUIRED:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.error,
            message: __('Please log in to your account'),
            timeout: 3000,
          })
          break
        case ParamPostModerationActionResult.MODERATE_FAIL:
          useGlobalSnackbarStore().setSnackbar({
            notificationType: SnackbarNotificationType.error,
            message: __('Unable to moderate post'),
            timeout: 3000,
          })
          break
      }
      clearSearchParam('moderate_post_id', false)
      clearSearchParam('moderate_post_action', false)
    },
    handleSlideshowQrCodeParams(): void {
      clearSearchParam('utm_source', false)
      clearSearchParam('utm_medium', false)
    },

    updateUser({ commit }, user) {
      commit('UPDATE_USER', user)
    },

    // Check App License to see if user needs to be logged out.
    async validateGoogleAppLicense() {
      const { GoogleAppLicensingSettingsApi } = await import('@@/surface/api/google_app_licensing_settings')
      const validationResponse = await GoogleAppLicensingSettingsApi.checkLicense()
      const licenseStatus = (validationResponse?.data as JsonAPIResource<GoogleAppLicensingValidateResponse>).attributes
      if (!licenseStatus.invalidLicense) return
      navigateTo(licenseStatus.nextUrl as string, { method: 'post' })
    },

    /* ------------
     * PANEL
     * ------------ */
    hideWallQuotaExceeded({ commit }) {
      commit('HIDE_WALL_QUOTA_EXCEEDED')
    },
    hideCommentsPanel({ commit }) {
      commit('HIDE_COMMENTS_PANEL')
    },
    showCommentsPanel({ commit }, { postId }) {
      commit('SHOW_COMMENTS_PANEL', postId)
    },
    /* ------------
     * POST
     * ------------ */
    deletePostRemote({ dispatch, state }, post) {
      if (state.commentsPanelPostId == post.id) {
        dispatch('hideCommentsPanel')
      }
      // postAction/deletePostRemote must come before post/deletePostRemote
      // the post action panels(e.g.post action menu, copy / transfer panels) hold their own cid value and will try to access getPostByCid(cid)
      // if we delete the post first, it will cause some undefined object errors
      useSurfacePostActionStore().deletePostRemote(post)
      dispatch('post/deletePostRemote', post)
    },
    removePost({ dispatch }, { id, cid }) {
      dispatch('post/removePost', { id, cid })
    },
    addPost({ dispatch }, payload) {
      const postAttributes = payload.post
      dispatch('post/addPost', postAttributes)
    },

    /* ------------
     * WALL
     * ------------ */
    updateWall({ commit }, changes) {
      commit('UPDATE_WALL', changes)
    },
    /* ------------
     * POST CONNECTION
     * ------------ */
    fallbackToRestApi({ dispatch, commit }) {
      commit('SET_SHOULD_USE_REST_API', true)
      setScope({ fetchingMode: 'REST' })
      // We add a setTimeout since we want to delay the execution of the code below until pinia is available
      setTimeout(() => {
        void dispatch('refresh', { isAutoRefresh: false })
      }, 0)
    },

    revertToWs({ dispatch, commit }) {
      commit('SET_SHOULD_USE_REST_API', false)
      setScope({ fetchingMode: 'WebSocket' })
      // We add a setTimeout since we want to delay the execution of the code below until pinia is available
      setTimeout(() => {
        void dispatch('refresh', { isAutoRefresh: false })
      }, 0)
    },

    // REFRESH
    refresh({ commit, dispatch, state, getters }, { isAutoRefresh }) {
      if (!useSurfaceStore().isOnline) return
      if (!getters.isWallSyncNeeded) return

      // `refresh` will apply all the pending realtime updates.
      // This is not immune to race conditions, but since it is harmless to omit this is the first place,
      // this is sufficient to reduce user annoyance due to outdated notifications.
      dispatch('realtime/clearDispatchQueue')

      // We are not updating the vuexstore on auto refresh, the network response will update service worker cache
      // and if the cache is update, it will send a notification to update the store

      const shouldResetState = !isAutoRefresh

      if (isAppUsing('fullClientSurface')) {
        // For FCS, we use the preloaded wishes api to fetch the first page of wishes
        void dispatch('post/fetchPosts', { shouldResetState })
      }

      if (state.shouldFallbackWsDataFetchToRest || !isAppUsing('realtimeFetching')) {
        void useReactionsStore().fetchAccumulatedReactions()

        if (!isAppUsing('surfaceCommentsPerPost')) {
          useCommentsStore().fetchComments({ shouldResetState })
        }

        if (!isAppUsing('fullClientSurface')) {
          void dispatch('post/fetchPosts', { shouldResetState })
        }

        void useSurfaceSectionsStore().fetchSections()
      }

      if (useSurfaceStore().canHaveConnections) {
        void useSurfacePostConnectionStore().fetchConnections()
      }

      useSurfaceContributingStatusStore().ping()

      // reactions, comments, wall sections, ... only do 1 request
      // but wishes have pagination and may make multiple requests
      // => for first page load, wait for it to be done before marking things as done
      if (getters.isInitialPaginatedDataLoadDone) {
        commit('REFRESH_DONE')
      } else {
        const unwatch = this.watch(
          (state, getters) => getters.isInitialPaginatedDataLoadDone,
          (newValue) => {
            if (newValue) {
              // To ensure that the wall content are loaded before translating the padlet
              if (isAppUsing('translatePadlet') && !getters.isWhiteboard) {
                const lang = getSearchParam(TRANSLATION_QUERY_PARAM_KEY)
                if (lang != null) {
                  void useSurfaceTranslationsStore().translatePadletFromBanner(lang)
                }
              }
              commit('REFRESH_DONE')
              unwatch()
            }
          },
        )
      }
    },
    // SNACKBAR
    snackbar(_, snackbarPayload) {
      useGlobalSnackbarStore().setSnackbar(snackbarPayload)
    },
    genericFetchError(_, { error }) {
      captureNonNetworkFetchError(error)
      useGlobalSnackbarStore().genericFetchError()
    },
  },
}

// Define a local type for now
interface BrowserWindow {
  ww?: any
}
const thisWindow = window as unknown as BrowserWindow

/**
 * The old dispatch function signature is different from the vuex store dispatch function signature
 * We write the dispatch function in ww.xstore so that any method signature will work.
 * ```js
 * ww.xstore.dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})
 * ww.xstore.dispatch('START_EDITING_POST', {postCid: id})
 * ww.xstore.dispatch(function (dispatch) => {dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})}
 * ```
 */

export function setupNativeBridgeXstore(store): void {
  const oldDispatch = (obj) => store.dispatch(obj.type, obj)

  if (!thisWindow.ww) {
    thisWindow.ww = {}
  }
  thisWindow.ww.xstore = {
    store,
    state: {},
    getters: {},
    dispatch: (obj, payload) => {
      if (typeof obj === 'string') {
        store.dispatch(obj, payload)
      } else if (obj.type) {
        oldDispatch(obj)
      } else if (typeof obj === 'function') {
        obj(oldDispatch)
      }
    },
  }

  store.subscribe(() => {
    thisWindow.ww.xstore.state = store.state
    thisWindow.ww.xstore.getters = store.getters
  })
}
